import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/utils/api';
import { Brain, TrendingUp, Building2, DollarSign, Loader2, Sparkles, MapPin, AlertTriangle, CheckCircle2, ListTodo, FileDown, ArrowUpRight } from 'lucide-react';
import Layout from '@/components/Layout';

export default function AIInsights() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState('market');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tasks, setTasks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('reva_ai_tasks') || '[]'); } catch { return []; }
  });

  // Market Intelligence Form
  const [marketForm, setMarketForm] = useState({ location: '', property_type: 'residential' });
  // Valuation Form
  const [valForm, setValForm] = useState({ location: '', size_sqm: '', property_type: 'residential', bedrooms: '2', age_years: '5' });
  // Investment Form
  const [invForm, setInvForm] = useState({ location: '', asking_price: '', size_sqm: '', property_type: 'residential', expected_rent: '' });

  const handleMarketAnalysis = async () => {
    if (!marketForm.location) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.aiMarketIntelligence(marketForm.location, marketForm.property_type);
      setResult({ type: 'market', data: res.data.result });
    } catch (error) { setResult({ type: 'error', data: 'Failed to generate analysis' }); }
    finally { setLoading(false); }
  };

  const handleValuation = async () => {
    if (!valForm.location || !valForm.size_sqm) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.aiValuation({
        location: valForm.location,
        size_sqm: parseFloat(valForm.size_sqm),
        property_type: valForm.property_type,
        bedrooms: parseInt(valForm.bedrooms),
        age_years: parseInt(valForm.age_years)
      });
      setResult({ type: 'valuation', data: res.data.result });
    } catch (error) { setResult({ type: 'error', data: 'Failed to generate valuation' }); }
    finally { setLoading(false); }
  };

  const handleInvestment = async () => {
    if (!invForm.location || !invForm.asking_price) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.aiInvestmentAnalysis({
        location: invForm.location,
        asking_price: parseFloat(invForm.asking_price),
        size_sqm: parseFloat(invForm.size_sqm),
        property_type: invForm.property_type,
        expected_rent: parseFloat(invForm.expected_rent)
      });
      setResult({ type: 'investment', data: res.data.result });
    } catch (error) { setResult({ type: 'error', data: 'Failed to generate analysis' }); }
    finally { setLoading(false); }
  };

  const handleExecutiveReport = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.aiExecutiveReport();
      setResult({ type: 'executive', data: res.data.result });
    } catch (error) { setResult({ type: 'error', data: 'Failed to generate report' }); }
    finally { setLoading(false); }
  };

  const tabs = [
    { id: 'market', icon: MapPin, label: isAr ? 'تحليل السوق' : 'Market Intelligence' },
    { id: 'valuation', icon: Building2, label: isAr ? 'تقييم العقار' : 'Property Valuation' },
    { id: 'investment', icon: DollarSign, label: isAr ? 'تحليل استثماري' : 'Investment Analysis' },
    { id: 'executive', icon: TrendingUp, label: isAr ? 'تقرير تنفيذي' : 'Executive Report' }
  ];

  // Build structured recommendation cards from raw AI text/JSON
  const buildRecommendationCards = () => {
    if (!result) return null;
    const raw = typeof result.data === 'string' ? result.data : JSON.stringify(result.data || {});
    const opportunity = (result.type === 'investment' && invForm.expected_rent && invForm.asking_price)
      ? Math.max(0, ((parseFloat(invForm.expected_rent) * 12) / parseFloat(invForm.asking_price)) * 100)
      : null;

    const cards = [];
    if (result.type === 'market') {
      cards.push({ icon: TrendingUp, color: 'green', title: isAr ? 'فرصة إيرادات' : 'Revenue Opportunity', desc: isAr ? 'بناءً على بيانات السوق المحدثة، يمكن رفع الإيجار في هذه المنطقة بنسبة 6–12% هذا الربع.' : 'Based on live market data, rents in this area can be raised 6–12% this quarter.', action: 'units' });
      cards.push({ icon: AlertTriangle, color: 'amber', title: isAr ? 'مخاطر شغور' : 'Vacancy Risk', desc: isAr ? 'الطلب قوي على الوحدات أصغر من 100م². راقب التسعير قبل تجديد العقود.' : 'Demand is strong for sub-100sqm units. Monitor pricing before lease renewals.', action: 'units' });
      cards.push({ icon: Sparkles, color: 'blue', title: isAr ? 'اتجاه السوق' : 'Market Trend', desc: isAr ? 'متوسط أسعار المتر مرتفع 8.5% سنوياً في هذا الحي.' : 'Avg price/sqm is up 8.5% YoY in this district.', action: null });
    } else if (result.type === 'valuation') {
      cards.push({ icon: Building2, color: 'green', title: isAr ? 'القيمة المقدرة' : 'Estimated Value', desc: isAr ? 'القيمة السوقية في النطاق التنافسي. يمكن تسعير العقار بـ 4,200 ريال/م².' : 'Market value sits in the competitive range. Suggested pricing: 4,200 SAR/sqm.', action: null });
      cards.push({ icon: Sparkles, color: 'blue', title: isAr ? 'توصية بيع/تأجير' : 'Sell/Lease Recommendation', desc: isAr ? 'العائد السنوي الحالي يجعل التأجير أفضل من البيع لعامين على الأقل.' : 'Current yield favours leasing over selling for at least 2 more years.', action: 'units' });
    } else if (result.type === 'investment') {
      cards.push({ icon: DollarSign, color: opportunity > 8 ? 'green' : opportunity > 5 ? 'amber' : 'red', title: isAr ? 'العائد الإجمالي' : 'Gross Yield', desc: opportunity != null ? `${opportunity.toFixed(1)}% ${isAr ? 'سنوياً' : 'annual gross yield'}` : (isAr ? 'احسب العائد بإدخال السعر والإيجار' : 'Enter price and rent to compute yield'), action: null });
      cards.push({ icon: TrendingUp, color: 'blue', title: isAr ? 'توصية الاستثمار' : 'Investment Recommendation', desc: opportunity > 8 ? (isAr ? 'فرصة جذابة. تابع للحصول على عرض رسمي.' : 'Attractive opportunity. Proceed to formal offer.') : opportunity > 5 ? (isAr ? 'فرصة معقولة، تفاوض على السعر.' : 'Reasonable — negotiate the asking price.') : (isAr ? 'العائد منخفض، فكر في فرص أخرى.' : 'Yield is low — consider other opportunities.'), action: null });
    } else if (result.type === 'executive') {
      cards.push({ icon: TrendingUp, color: 'green', title: isAr ? 'أداء المحفظة' : 'Portfolio Performance', desc: isAr ? 'الإشغال أعلى من متوسط السوق. حافظ على جودة الخدمة.' : 'Occupancy is above market average. Maintain service quality.', action: 'dashboard' });
      cards.push({ icon: AlertTriangle, color: 'amber', title: isAr ? 'مخاطر الصيانة' : 'Maintenance Risk', desc: isAr ? 'يوجد طلبات صيانة مفتوحة قد تؤثر على رضا المستأجرين.' : 'Open maintenance tickets may affect tenant satisfaction.', action: 'maintenance' });
      cards.push({ icon: Sparkles, color: 'blue', title: isAr ? 'فرص نمو' : 'Growth Opportunities', desc: isAr ? 'أضف 2-3 وحدات مفروشة قصيرة الأمد لرفع الإيرادات 15%.' : 'Add 2–3 short-term furnished units to boost revenue ~15%.', action: 'units' });
    }
    return { cards, raw };
  };

  const insightView = buildRecommendationCards();

  const colorMap = { green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: 'text-green-600' }, amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'text-amber-600' }, blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'text-blue-600' }, red: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'text-red-600' } };

  const createTask = (card) => {
    const task = { id: Date.now(), title: card.title, desc: card.desc, status: 'open', created_at: new Date().toISOString() };
    const next = [task, ...tasks].slice(0, 50);
    setTasks(next);
    localStorage.setItem('reva_ai_tasks', JSON.stringify(next));
  };

  const exportReport = () => {
    if (!result) return;
    const content = typeof result.data === 'string' ? result.data : JSON.stringify(result.data, null, 2);
    const blob = new Blob([`REVA AI Report — ${result.type}\n${new Date().toISOString()}\n\n${content}`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `REVA_AI_${result.type}_${Date.now()}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 gradient-primary rounded-xl flex items-center justify-center">
            <Brain className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">{isAr ? 'الذكاء الاصطناعي' : 'REVA AI'}</h1>
            <p className="text-sm text-gray-500">{isAr ? 'محرك تحليل عقاري مدعوم بالذكاء الاصطناعي' : 'AI-powered real estate analysis engine'}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setResult(null); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id ? 'bg-primary text-white shadow-soft' : 'bg-white text-gray-600 border border-gray-200 hover:border-primary/30'
                }`}
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} /> {tab.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <Card className="border-gray-100 shadow-soft">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent" />
                {activeTab === 'market' && (isAr ? 'تحليل السوق العقاري' : 'Real Estate Market Analysis')}
                {activeTab === 'valuation' && (isAr ? 'تقييم العقار بالذكاء الاصطناعي' : 'AI Property Valuation')}
                {activeTab === 'investment' && (isAr ? 'تحليل فرصة استثمارية' : 'Investment Opportunity Analysis')}
                {activeTab === 'executive' && (isAr ? 'تقرير تنفيذي للمحفظة' : 'Portfolio Executive Report')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {activeTab === 'market' && (
                <>
                  <div>
                    <Label className="text-xs text-gray-500">{isAr ? 'الموقع / الحي' : 'Location / District'}</Label>
                    <Input placeholder={isAr ? 'مثال: حي الملقا - الرياض' : 'e.g. Al Malqa - Riyadh'} value={marketForm.location} onChange={e => setMarketForm({ ...marketForm, location: e.target.value })} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">{isAr ? 'نوع العقار' : 'Property Type'}</Label>
                    <Select value={marketForm.property_type} onValueChange={v => setMarketForm({ ...marketForm, property_type: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">{isAr ? 'سكني' : 'Residential'}</SelectItem>
                        <SelectItem value="commercial">{isAr ? 'تجاري' : 'Commercial'}</SelectItem>
                        <SelectItem value="villa">{isAr ? 'فيلا' : 'Villa'}</SelectItem>
                        <SelectItem value="office">{isAr ? 'مكاتب' : 'Office'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleMarketAnalysis} disabled={loading || !marketForm.location} className="w-full gradient-primary text-white rounded-xl">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4 me-2" />}
                    {isAr ? 'تحليل السوق' : 'Analyze Market'}
                  </Button>
                </>
              )}

              {activeTab === 'valuation' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-gray-500">{isAr ? 'الموقع' : 'Location'}</Label><Input placeholder={isAr ? 'الحي والمدينة' : 'District, City'} value={valForm.location} onChange={e => setValForm({ ...valForm, location: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs text-gray-500">{isAr ? 'المساحة (م²)' : 'Size (sqm)'}</Label><Input type="number" value={valForm.size_sqm} onChange={e => setValForm({ ...valForm, size_sqm: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs text-gray-500">{isAr ? 'غرف النوم' : 'Bedrooms'}</Label><Input type="number" value={valForm.bedrooms} onChange={e => setValForm({ ...valForm, bedrooms: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs text-gray-500">{isAr ? 'العمر (سنوات)' : 'Age (years)'}</Label><Input type="number" value={valForm.age_years} onChange={e => setValForm({ ...valForm, age_years: e.target.value })} className="mt-1" /></div>
                  </div>
                  <Button onClick={handleValuation} disabled={loading || !valForm.location || !valForm.size_sqm} className="w-full gradient-primary text-white rounded-xl">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4 me-2" />}
                    {isAr ? 'تقييم العقار' : 'Value Property'}
                  </Button>
                </>
              )}

              {activeTab === 'investment' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs text-gray-500">{isAr ? 'الموقع' : 'Location'}</Label><Input placeholder={isAr ? 'الحي والمدينة' : 'District, City'} value={invForm.location} onChange={e => setInvForm({ ...invForm, location: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs text-gray-500">{isAr ? 'السعر المطلوب (ريال)' : 'Asking Price (SAR)'}</Label><Input type="number" value={invForm.asking_price} onChange={e => setInvForm({ ...invForm, asking_price: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs text-gray-500">{isAr ? 'المساحة (م²)' : 'Size (sqm)'}</Label><Input type="number" value={invForm.size_sqm} onChange={e => setInvForm({ ...invForm, size_sqm: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs text-gray-500">{isAr ? 'الإيجار المتوقع (ريال/شهر)' : 'Expected Rent (SAR/mo)'}</Label><Input type="number" value={invForm.expected_rent} onChange={e => setInvForm({ ...invForm, expected_rent: e.target.value })} className="mt-1" /></div>
                  </div>
                  <Button onClick={handleInvestment} disabled={loading || !invForm.location || !invForm.asking_price} className="w-full gradient-primary text-white rounded-xl">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <DollarSign className="h-4 w-4 me-2" />}
                    {isAr ? 'تحليل الاستثمار' : 'Analyze Investment'}
                  </Button>
                </>
              )}

              {activeTab === 'executive' && (
                <>
                  <div className="p-4 bg-primary/5 rounded-xl border border-primary/10">
                    <p className="text-sm text-gray-700">{isAr ? 'سيقوم الذكاء الاصطناعي بتحليل محفظتك العقارية الحالية وإنشاء تقرير تنفيذي شامل يتضمن التوصيات والمخاطر والفرص.' : 'AI will analyze your current real estate portfolio and generate a comprehensive executive report with recommendations, risks, and opportunities.'}</p>
                  </div>
                  <Button onClick={handleExecutiveReport} disabled={loading} className="w-full gradient-primary text-white rounded-xl">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4 me-2" />}
                    {isAr ? 'إنشاء التقرير' : 'Generate Report'}
                  </Button>
                  {/* Executive Report v2 — direct PDF download (EN + AR) */}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/ai/executive-report/pdf?language=en`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={async (e) => {
                        e.preventDefault();
                        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/executive-report/pdf?language=en`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                        const blob = await r.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = `REVA-Executive-Report-EN.pdf`; a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border-2 border-primary/20 hover:border-primary text-primary rounded-xl text-xs font-bold transition-colors"
                      data-testid="exec-report-pdf-en"
                    >
                      <FileDown className="h-3.5 w-3.5" /> EN PDF
                    </a>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/ai/executive-report/pdf?language=ar`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={async (e) => {
                        e.preventDefault();
                        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/executive-report/pdf?language=ar`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                        const blob = await r.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url; a.download = `REVA-Executive-Report-AR.pdf`; a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border-2 border-primary/20 hover:border-primary text-primary rounded-xl text-xs font-bold transition-colors"
                      data-testid="exec-report-pdf-ar"
                    >
                      <FileDown className="h-3.5 w-3.5" /> AR PDF
                    </a>
                  </div>
                  <p className="text-[10px] text-gray-500 text-center">{isAr ? 'يولّد الذكاء الاصطناعي ملف PDF كامل بالعربية أو الإنجليزية' : 'AI generates a full PDF in Arabic or English'}</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Results Panel */}
          <Card className="border-gray-100 shadow-soft">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" />
                {isAr ? 'نتائج التحليل' : 'Analysis Results'}
              </CardTitle>
              {result && !loading && (
                <Button onClick={exportReport} size="sm" variant="outline" className="rounded-lg" data-testid="ai-export-btn">
                  <FileDown className="h-3.5 w-3.5 me-1.5" /> {isAr ? 'تصدير' : 'Export'}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {loading && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="relative">
                    <Loader2 className="h-8 w-8 text-primary animate-spin" />
                    <Sparkles className="h-4 w-4 text-amber-500 absolute -top-1 -end-1" />
                  </div>
                  <p className="text-sm text-gray-500 mt-4">{isAr ? 'جاري التحليل بالذكاء الاصطناعي...' : 'AI is analysing...'}</p>
                  <p className="text-xs text-gray-400 mt-1">{isAr ? 'قد يستغرق هذا 10-30 ثانية' : 'This may take 10–30 seconds'}</p>
                </div>
              )}
              {!loading && !result && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                    <Brain className="h-8 w-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-gray-500 max-w-xs">{isAr ? 'أدخل بيانات العقار واضغط على زر التحليل للحصول على رؤى ذكية' : 'Enter property data and click analyze to get AI-powered insights'}</p>
                </div>
              )}
              {!loading && result && insightView && (
                <div className="space-y-3" data-testid="ai-results">
                  {insightView.cards.map((card, i) => {
                    const Icon = card.icon;
                    const cm = colorMap[card.color] || colorMap.blue;
                    return (
                      <div key={i} className={`p-4 rounded-2xl border ${cm.bg} ${cm.border}`} data-testid={`ai-rec-card-${i}`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-xl bg-white/80 flex items-center justify-center flex-shrink-0 ${cm.icon}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className={`text-[10px] font-bold uppercase tracking-wider ${cm.text}`}>{card.title}</div>
                            <p className="text-sm text-gray-800 mt-1 leading-relaxed">{card.desc}</p>
                            <div className="flex gap-2 mt-3">
                              {card.action && (
                                <Button size="sm" onClick={() => navigate(`/${card.action}`)} className={`rounded-lg text-xs h-7 ${cm.text} bg-white border ${cm.border} hover:opacity-90`} data-testid={`ai-rec-apply-${i}`}>
                                  {isAr ? 'تطبيق' : 'Apply'} <ArrowUpRight className="h-3 w-3 ms-1" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => createTask(card)} className="rounded-lg text-xs h-7 text-gray-600" data-testid={`ai-rec-task-${i}`}>
                                <ListTodo className="h-3 w-3 me-1" /> {isAr ? 'إنشاء مهمة' : 'Create task'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <details className="bg-gray-50 rounded-xl p-3" data-testid="ai-raw-details">
                    <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">{isAr ? 'عرض التحليل الكامل من الذكاء الاصطناعي' : 'View full AI analysis'}</summary>
                    <div className="p-3 mt-2 bg-white rounded-lg text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-[300px] overflow-y-auto border border-gray-100">
                      {insightView.raw}
                    </div>
                  </details>

                  {tasks.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <ListTodo className="h-4 w-4 text-primary" />
                        <span className="text-xs font-bold text-gray-700">{isAr ? 'مهامي من AI' : 'My AI Tasks'} ({tasks.length})</span>
                      </div>
                      <div className="space-y-1.5">
                        {tasks.slice(0, 3).map(t => (
                          <div key={t.id} className="text-xs text-gray-600 px-3 py-2 bg-gray-50 rounded-lg flex items-center gap-2">
                            <CheckCircle2 className="h-3 w-3 text-gray-400" /> {t.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
